import { ChangeDetectorRef, Component, HostListener, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { ColumnApi, GridApi, GridOptions } from 'ag-grid-community';
import { ProjectDetailsApiService } from '../../services/project-details-api.service';
import { ProjectDetailsStateService } from '../../services/project-details-state.service';
import { ProjectDetailsGridConfigModel } from '../../../common/models/project-details-grid-config.model';
import { GridDatePickerComponent } from '../../../common/custom-components/ag-grid-datepicker-component';
import { ThemeAllocationRow, PrepaymentDetailRow } from '../../models/project-details.types';

@Component({
  selector: 'app-theme-allocation-dailog',
  templateUrl: './theme-allocation-dailog.component.html',
  styleUrls: ['./theme-allocation-dailog.component.scss']
})
export class ThemeAllocationDailogComponent implements OnInit {
  gridType: 'themeAllocation' | 'prepaymentDetails' = 'themeAllocation';
  mode: string = 'edit';

  themeAllocationRowData: ThemeAllocationRow[] = [];
  themeAllocationColumnDefs: any[] = [];

  prepaymentDetailsRowData: PrepaymentDetailRow[] = [];
  prepaymentDetailsColumnDefs: any[] = [];

  gridOptions: GridOptions = {};
  rowSelection: string = 'multiple';
  agGridAPI!: GridApi;
  agGridColumnAPI!: ColumnApi;

  isSaveEnabled: boolean = false;
  isDeleteEnabled: boolean = false;
  private isEditing: boolean = false;
  themeList: string[] = [];

  components = {
    GridDatePickerComponent: GridDatePickerComponent
  };

  constructor(
    public dialogRef: MatDialogRef<ThemeAllocationDailogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private cdr: ChangeDetectorRef,
    private apiService: ProjectDetailsApiService,
    private stateService: ProjectDetailsStateService
  ) {
    if (data) {
      this.gridType = data.gridType || 'themeAllocation';
      this.mode = data.mode || 'edit';
      this.themeList = data.themeList || [];

      console.log('Theme List received in dialog:', this.themeList);
      console.log('First theme:', this.themeList[0]);
      console.log('Dialog data:', data);
    }
  }

  ngOnInit(): void {
    this.setupGridConfigs();
    this.loadData();
    this.cdr.detectChanges();
  }

  setupGridConfigs(): void {
    const gridConfig = new ProjectDetailsGridConfigModel();

    this.themeAllocationColumnDefs = gridConfig.themeAllocationColumns.map((col: any) => ({
      headerName: col.headerName.split('_').join(' '),
      field: col.field,
      resizable: true,
      sortable: true,
      editable: this.mode === 'view' ? false : col.editable,
      cellStyle: col.cellStyle,
      checkboxSelection: this.mode === 'view' ? false : col.checkboxSelection,
      headerCheckboxSelection: this.mode === 'view' ? false : col.headerCheckboxSelection,
      hide: !!col.hide,
      cellEditor: col.cellEditor || null,
      cellEditorParams: col.cellEditorParams ? {
        ...col.cellEditorParams,
        values: this.themeList.length > 0
          ? this.themeList.map((theme: any) => theme.themeDesc)
          : []
      } : null,
      flex: 1
    }));

    this.prepaymentDetailsColumnDefs = gridConfig.prepaymentDetailsColumns.map((col: any) => ({
      headerName: col.headerName.split('_').join(' '),
      field: col.field,
      resizable: true,
      sortable: true,
      editable: this.mode === 'view' ? false : col.editable,
      cellStyle: col.cellStyle,
      checkboxSelection: this.mode === 'view' ? false : col.checkboxSelection,
      headerCheckboxSelection: this.mode === 'view' ? false : col.headerCheckboxSelection,
      hide: !!col.hide,
      cellEditor: col.cellEditor || null,
      cellEditorParams: col.cellEditorParams || null,
      valueFormatter: col.valueFormatter || null,
      flex: 1
    }));

    this.gridOptions = {
      rowSelection: 'multiple',
      groupDefaultExpanded: 1,
      groupSelectsChildren: true,
      suppressAggFuncInHeader: true,
      getRowStyle: (params) => {
        if (params.node.isSelected()) {
          return { background: 'lightblue' };
        }
        if (params.node.data?.dirty) {
          return { background: '#ffc107' };
        }
        return undefined;
      }
    };
  }

  loadData(): void {

    const tableData =
      this.stateService.getTableData();

    if (this.gridType === 'themeAllocation') {

      this.themeAllocationRowData =
        tableData.projectThemeAllocation
          ? tableData.projectThemeAllocation.map(
              (row: any) => ({
                ...row,

                // Store original theme BEFORE user edits it
                originalTheme: row.Theme
              })
            )
          : [];

    } else {

      this.prepaymentDetailsRowData =
        tableData.projectPrepaymentDetails
          ? [
              ...tableData.projectPrepaymentDetails
            ]
          : [];

    }
  }

  onGridReady(event: any): void {
    this.agGridAPI = event.api;
    this.agGridColumnAPI = event.columnApi;
    this.autoSizeAllColumns();
  }

  autoSizeAllColumns(): void {
    if (!this.agGridColumnAPI) return;
    const allColumnIds: string[] = [];
    this.agGridColumnAPI.getColumns()?.forEach(col => {
      allColumnIds.push(col.getId());
    });
  }

  onCellValueChanged(event: any): void {
    event.data.isModified = true;
    event.data.dirty = true;
    event.api.applyTransaction({ update: [event.data] });
    event.api.redrawRows({ rowNodes: [event.node] });
    this.isSaveEnabled = true;
    this.stateService.setUnsavedChanges(true);
  }

  onSelectionChanged(event: any): void {
    const selectedNodes = event.api.getSelectedNodes();
    this.isDeleteEnabled = selectedNodes.length > 0;
    event.api.redrawRows({ rowNodes: selectedNodes });
  }

  onCellEditingStarted(event: any): void {
    this.isEditing = true;
  }

  onCellEditingStopped(event: any): void {
    this.isEditing = false;
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (this.isEditing && !this.isClickInsideGrid(event)) {
      this.agGridAPI?.stopEditing();
    }
  }

  private isClickInsideGrid(event: MouseEvent): boolean {
    const gridElement = document.querySelector('.ag-theme-balham');
    return gridElement?.contains(event.target as Node) ?? false;
  }

  async onSaveClickTA(): Promise<void> {
    const clientCode = this.stateService.getSelectedClientCode();
    const projectCode = this.stateService.getSelectedProjectCode();
    const modifiedRows: any[] = [];

    this.agGridAPI.forEachNode(node => {
      if (node.data?.isModified) {
        modifiedRows.push(node.data);
      }
    });

    if (modifiedRows.length === 0) return;

    const payloadArray = [];
    for (const row of modifiedRows) {
      const themeCode = await this.apiService.getProjectThemeCode(row.Theme);
      payloadArray.push({
        client: clientCode,
        project: projectCode,
        allocationPercent: parseInt(row.Allocation_Percent, 10),
        theme: themeCode,
        originalTheme: row.originalTheme || row.Theme
      });
    }

    this.apiService.updateThemeAllocation(payloadArray).subscribe({
      next: (res) => {
        this.stateService.showNotification(res);
        this.isSaveEnabled = false;
        this.refreshTables(clientCode, projectCode);
      },
      error: (err) => {
        this.stateService.showNotification(err.error || { Type: 'failure', Message: 'Failed to update theme allocation' });
      }
    });
  }

  async onDeleteClick(): Promise<void> {
    const clientCode = this.stateService.getSelectedClientCode();
    const projectCode = this.stateService.getSelectedProjectCode();
    const selectedRows = this.agGridAPI.getSelectedRows();

    if (selectedRows.length === 0) return;

    const payloadArray = selectedRows.map(row => ({
      Client: clientCode,
      Project: projectCode,
      Theme: row.Theme
    }));

    this.apiService.deleteThemeAllocation(payloadArray).subscribe({
      next: (res) => {
        this.stateService.showNotification(res);
        this.isDeleteEnabled = false;
        this.refreshTables(clientCode, projectCode);
      },
      error: (err) => {
        this.stateService.showNotification(err.error || { Type: 'failure', Message: 'Failed to delete theme allocation' });
      }
    });
  }
  

  async onSaveClickPD(): Promise<void> {
    const clientCode = this.stateService.getSelectedClientCode();
    const projectCode = this.stateService.getSelectedProjectCode();
    const modifiedRows: any[] = [];

    this.agGridAPI.forEachNode(node => {
      if (node.data?.isModified) {
        modifiedRows.push(node.data);
      }
    });

    if (modifiedRows.length === 0) return;

    const row = modifiedRows[0];
    const payload = {
      ID: row.ID,
      client: clientCode,
      project: projectCode,
      prepaymentAmt: parseInt(row.Prepayment_Amount, 10),
      referenceNo: row.Reference_Number,
      prepaymentDate: row.Prepayment_Date,
      description: row.Description
    };

    this.apiService.updatePrepaymentDetails(payload).subscribe({
      next: (res) => {
        this.stateService.showNotification(res);
        this.isSaveEnabled = false;
        this.refreshTables(clientCode, projectCode);
      },
      error: (err) => {
        this.stateService.showNotification(err.error || { Type: 'failure', Message: 'Failed to update prepayment details' });
      }
    });
  }

  private async refreshTables(clientCode: string, projectCode: string): Promise<void> {
    try {
      const data = await this.apiService.getProjectDetailsTables(clientCode, projectCode);
      this.stateService.setTableData(data);
      this.loadData();
    } catch (e) {
      console.error('Error refreshing tables:', e);
    }
  }
}